// Canonical sharing-UI template — optional generic artifact renderer.
// A dependency-free ArtifactRenderer for apps without their own report component:
// parses inputJson + outputJson and renders them as labelled key/value sections.
// Apps with a richer report can ignore this and pass their own renderer instead.

import React from "react";
import type { SharedArtifact } from "./types";

function renderValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return <span style={{ color: "var(--sm-text-muted, #64748b)" }}>—</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: "var(--sm-text-muted, #64748b)" }}>[]</span>;
    return (
      <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
        {value.map((v, i) => <li key={i}>{renderValue(v)}</li>)}
      </ul>
    );
  }
  if (typeof value === "object") return <ObjectRows obj={value as Record<string, unknown>} />;
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  return String(value);
}

const ObjectRows: React.FC<{ obj: Record<string, unknown> }> = ({ obj }) => (
  <table className="sm-table" style={{ width: "100%" }}>
    <tbody>
      {Object.entries(obj).map(([key, val]) => (
        <tr key={key}>
          <td style={{ fontWeight: 600, verticalAlign: "top", width: "40%" }}>{key}</td>
          <td>{renderValue(val)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Section: React.FC<{ heading: string; json: string }> = ({ heading, json }) => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return (
      <>
        <h3>{heading}</h3>
        <pre className="sm-error-details">{json}</pre>
      </>
    );
  }
  return (
    <>
      <h3>{heading}</h3>
      {renderValue(parsed)}
    </>
  );
};

/** Drop-in ArtifactRenderer: renderArtifact={renderGenericArtifact}. */
export function renderGenericArtifact(artifact: SharedArtifact): React.ReactNode {
  return (
    <>
      <Section heading="Inndata" json={artifact.inputJson} />
      <div style={{ marginTop: "1rem" }}>
        <Section heading="Resultat" json={artifact.outputJson} />
      </div>
    </>
  );
}
